import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './FilterMenu.css';

// The filter control lives in the table toolbar: a single compact "Filtros · N"
// button that opens a management popover (active criteria + "Añadir filtro"),
// instead of cramming filter pills into the bar. Server-backed — `onChange`
// emits the next `{ [key]: value }` map and the route re-queries.
// across every list table.
//
//   definitions: [{ key, label, glyph, options?, ready?, searchable?, multi? }]
//   value:       { [key]: selectedValue | selectedValue[] }  // set keys = "active"
//   onChange:    (nextValue) => void
//
// `multi: true` habilita selección múltiple para ese filtro: las opciones se
// togglean (el picker queda abierto para seguir marcando) y el valor emitido
// es un ARRAY — el route lo serializa CSV hacia la API (`a,b,c`).
export function FilterMenu({ definitions, value, onChange }) {
  const [open, setOpen] = useState(false);
  // 'list' = active filters · 'add' = property picker · a key = value picker
  const [stage, setStage] = useState('list');
  // Filter text for searchable value pickers (e.g. Tribunal's long list).
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  // Reset the value-picker search whenever the stage changes.
  useEffect(() => setQuery(''), [stage]);

  useEffect(() => {
    if (!open) return undefined;
    // Detect outside clicks on mousedown, NOT click: a stage transition (e.g.
    // "Añadir filtro" → property list) unmounts the clicked button, and React
    // flushes that synchronously before a `click` listener on document runs —
    // so `contains(e.target)` would see a detached node and wrongly close. On
    // mousedown the DOM is still intact, so inside-clicks are detected right.
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Valores seleccionados de un filtro, siempre como array (los filtros single
  // guardan un escalar; los `multi` un array).
  const vals = (key) => {
    const v = value[key];
    if (v == null) return [];
    return Array.isArray(v) ? v : [v];
  };

  const active = definitions.filter((d) => vals(d.key).length > 0);
  const count = active.length;

  function toggle() {
    setStage('list');
    setOpen((v) => !v);
  }

  function valueLabel(def) {
    const selected = vals(def.key);
    const labelOf = (v) => def.options?.find((o) => o.value === v)?.label ?? v;
    if (selected.length <= 1) return labelOf(selected[0]);
    return `${labelOf(selected[0])} +${selected.length - 1}`;
  }

  function setFilter(key, optValue) {
    onChange({ ...value, [key]: optValue });
    setStage('list');
  }

  // Multi: togglea la opción y deja el picker ABIERTO para seguir marcando.
  // Quitar el último valor desactiva el filtro (la key sale del mapa).
  function toggleFilter(key, optValue) {
    const selected = vals(key);
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    const nextValue = { ...value };
    if (next.length === 0) delete nextValue[key];
    else nextValue[key] = next;
    onChange(nextValue);
  }

  function removeFilter(key) {
    const next = { ...value };
    delete next[key];
    onChange(next);
  }

  // One step back inside the popover (value picker → property list → active
  // filters) so you don't have to close and reopen to change course.
  function goBack() {
    setStage((s) => (s === 'add' ? 'list' : 'add'));
  }

  const pickDef = definitions.find((d) => d.key === stage);

  return (
    <span className="filtermenu" ref={ref}>
      <button
        type="button"
        className={`filtermenu__btn${count ? ' is-active' : ''}`}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="filtermenu__ico" aria-hidden="true">
          ⚲
        </span>
        Filtros
        {count > 0 && <span className="filtermenu__count">{count}</span>}
      </button>

      {open && (
        <div className="filtermenu__pop" role="menu">
          {stage !== 'list' && (
            <button type="button" className="filtermenu__back" onClick={goBack}>
              ‹ Volver
            </button>
          )}
          {stage === 'list' && (
            <>
              {count > 0 && (
                <>
                  <div className="filtermenu__label">Filtros activos</div>
                  {active.map((def) => (
                    <div key={def.key} className="filtermenu__frow">
                      <span className="filtermenu__fprop">{def.label}</span>
                      <span className="filtermenu__fop">=</span>
                      <button
                        type="button"
                        className="filtermenu__fval"
                        onClick={() => setStage(def.key)}
                      >
                        {valueLabel(def)}
                      </button>
                      <button
                        type="button"
                        className="filtermenu__fx"
                        aria-label={`Quitar filtro ${def.label}`}
                        onClick={() => removeFilter(def.key)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="filtermenu__sep" />
                </>
              )}
              <button
                type="button"
                className="filtermenu__add"
                onClick={() => setStage('add')}
              >
                ＋ Añadir filtro
              </button>
            </>
          )}

          {stage === 'add' && (
            <>
              <div className="filtermenu__label">Filtrar por</div>
              {definitions.map((def) => (
                <button
                  key={def.key}
                  type="button"
                  className={`filtermenu__item${def.ready === false ? ' is-disabled' : ''}`}
                  disabled={def.ready === false}
                  onClick={() => def.ready !== false && setStage(def.key)}
                >
                  <span className="filtermenu__gly" aria-hidden="true">
                    {def.glyph}
                  </span>
                  {def.label}
                  {def.ready === false && (
                    <span className="filtermenu__soon">próx.</span>
                  )}
                  {vals(def.key).length > 0 && (
                    <span className="filtermenu__chk">✓</span>
                  )}
                </button>
              ))}
            </>
          )}

          {pickDef && (
            <>
              <div className="filtermenu__label">{pickDef.label}</div>
              {pickDef.searchable && (
                <input
                  type="search"
                  className="filtermenu__search"
                  placeholder={`Buscar ${pickDef.label.toLowerCase()}…`}
                  value={query}
                  autoFocus
                  onChange={(e) => setQuery(e.target.value)}
                />
              )}
              <div className="filtermenu__scroll">
                {(pickDef.options ?? [])
                  .filter(
                    (o) =>
                      !query ||
                      (o.label ?? '')
                        .toLowerCase()
                        .includes(query.toLowerCase()),
                  )
                  .map((o) => {
                    const isOn = vals(pickDef.key).includes(o.value);
                    return (
                      <button
                        key={o.value}
                        type="button"
                        className={`filtermenu__item${isOn ? ' is-on' : ''}`}
                        onClick={() =>
                          pickDef.multi
                            ? toggleFilter(pickDef.key, o.value)
                            : setFilter(pickDef.key, o.value)
                        }
                      >
                        {o.label}
                        {isOn && <span className="filtermenu__chk">✓</span>}
                      </button>
                    );
                  })}
                {pickDef.searchable && (pickDef.options ?? []).length === 0 && (
                  <div className="filtermenu__empty">Sin opciones</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}

FilterMenu.propTypes = {
  definitions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      glyph: PropTypes.string,
      ready: PropTypes.bool,
      searchable: PropTypes.bool,
      multi: PropTypes.bool,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

FilterMenu.defaultProps = {
  value: {},
};

export default FilterMenu;
